import React, { useState, useEffect } from 'react';
import { Search, CheckCircle2, RefreshCw, Loader2, Download } from 'lucide-react';
import api from '../../utils/api';
import PackageSearchSelect from './PackageSearchSelect';

const UNMATCHED_LABEL_VALUE = '__UNMATCHED__';

const PAY_TYPE_OPTIONS = [
  { value: 'all', label: '全部支付方式' },
  { value: 'wechat', label: '微信支付' },
  { value: 'alipay', label: '支付宝' }
];

export default function PartialRefundDetailsTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filterPayType, setFilterPayType] = useState('all');
  const [filterPhone, setFilterPhone] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterUserLabel, setFilterUserLabel] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);
  const [jumpPage, setJumpPage] = useState('');

  const fetchOrders = async ({
    page = 1,
    phone = filterPhone,
    payType = filterPayType,
    userLabel = filterUserLabel
  } = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/partial-refund-orders', {
        params: {
          page,
          limit: pagination.limit,
          phone: phone || undefined,
          payType: payType !== 'all' ? payType : undefined,
          userLabel: userLabel || undefined
        }
      });
      if (response.data.success) {
        const data = response.data.data || response.data;
        setOrders(data.list || []);
        setLabelOptions(Array.isArray(data.labelOptions) ? data.labelOptions : []);
        setPagination({
          page: data.page,
          limit: data.limit,
          total: data.total,
          totalPages: data.totalPages
        });
      }
    } catch (error) {
      console.error('Failed to fetch partial refund orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders({
      page: 1,
      phone: filterPhone,
      payType: filterPayType,
      userLabel: filterUserLabel
    });
  }, [filterPayType, filterPhone, filterUserLabel]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilterPhone(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setFilterPhone('');
    setFilterPayType('all');
    setFilterUserLabel('');
    setJumpPage('');
  };

  const handleJump = () => {
    const page = Number(jumpPage);
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('');
      return;
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`);
      return;
    }
    fetchOrders({ page });
    setJumpPage('');
  };

  const labelSelectOptions = [
    { id: 'partial-refund-label-unmatched', value: UNMATCHED_LABEL_VALUE, label: '未匹配' },
    ...labelOptions.map((label, index) => ({
      id: `partial-refund-label-${index}`,
      value: label,
      label
    }))
  ];

  const getPayTypeLabel = (payType) => {
    if (payType === 'wechat') return '微信支付';
    if (payType === 'alipay') return '支付宝';
    if (payType === 'hzbank') return '联合银行';
    if (payType === 'ly360') return '360支付';
    if (payType === 'ly360top') return '360支付(top)';
    if (payType === 'aliyun_market') return '阿里云市场';
    return payType || '-';
  };

  const getStatusBadge = (status) => {
    if (status === 1) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1"/> 已支付</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">未知</span>;
  };

  const renderUserLabels = (labels) => {
    const normalizedLabels = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (normalizedLabels.length <= 0) {
      return <span className="text-xs text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-col items-start gap-2">
        {normalizedLabels.map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/partial-refund-orders/export-csv', {
        params: {
          phone: filterPhone || undefined,
          payType: filterPayType !== 'all' ? filterPayType : undefined,
          userLabel: filterUserLabel || undefined
        },
        responseType: 'blob',
        timeout: 0
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const rawFilename = utf8Match?.[1] || plainMatch?.[1] || `退款明细_${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = decodeURIComponent(rawFilename);

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('导出退款明细失败:', error);
      alert('导出失败，请稍后重试');
    }
  };

  const hasActiveFilters = !!filterPhone || filterPayType !== 'all' || !!filterUserLabel;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">退款明细</h2>

        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* 支付方式筛选 */}
          <select
            value={filterPayType}
            onChange={(e) => setFilterPayType(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
          >
            {PAY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <PackageSearchSelect
            value={filterUserLabel}
            onChange={(value) => setFilterUserLabel(value)}
            options={labelSelectOptions}
            placeholder="全部标签"
            className="w-full sm:w-52 min-w-0"
            getOptionValue={(item) => item.value}
            getOptionDisplayName={(item) => item.label}
            searchPlaceholder="搜索用户标签"
          />

          {/* 手机号/设备号搜索 */}
          <form onSubmit={handleSearch} className="flex flex-1 sm:flex-none items-center gap-2">
            <div className="relative flex-1 sm:w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="搜索手机号/设备号"
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-white"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm whitespace-nowrap"
              >
                重置
              </button>
            )}
          </form>

          <button
            onClick={() => fetchOrders({ page: pagination.page })}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ml-auto sm:ml-0"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">刷新</span>
          </button>
          <button
            onClick={handleExport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">导出 CSV</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">订单号/流水号</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">付款用户</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">标签</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">渠道来源</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">原支付金额</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">已退金额</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">剩余金额</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">支付方式</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">退款原因</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">退款时间</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-gray-500">暂无退款明细数据</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{order.order_no}</div>
                      <div className="text-xs text-gray-500">{order.transaction_id || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white" title={order.nickname || order.username}>
                        ID: {order.user_id}
                      </div>
                      <div className="text-xs text-gray-500">{order.phone || '-'}</div>
                    </td>
                    <td className="px-4 py-4">
                      {renderUserLabels(order.user_labels)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {order.channel_label || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ¥{Number(order.original_amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                      ¥{Number(order.refunded_amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      ¥{Number(order.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getPayTypeLabel(order.pay_type)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-4 py-4">
                      {Array.isArray(order.refund_reasons) && order.refund_reasons.length > 0 ? (
                        <div className="flex flex-col items-start gap-1">
                          {order.refund_reasons.map((reason, idx) => (
                            <div key={idx} className="text-sm text-gray-700">
                              <span className="font-medium text-red-600">¥{Number(reason.refund_amount).toFixed(2)}</span>
                              <span className="text-gray-500"> - {reason.remark || '未填写'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {order.last_refund_at ? (
                        <>
                          <div className="text-sm text-gray-900 dark:text-white">{new Date(order.last_refund_at).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{new Date(order.last_refund_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              共 {pagination.total} 条记录
            </span>
            <div className="flex gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                onClick={() => fetchOrders({ page: pagination.page - 1 })}
                disabled={pagination.page === 1 || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                上一页
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchOrders({ page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages || loading}
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-md text-sm disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
